import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import ImageUpload from '@/components/ui/ImageUpload';
import { Skeleton } from '@/components/ui/skeleton';
import { Building2, Check, Info, Palette, SlidersHorizontal } from 'lucide-react';
import { toast } from '@/hooks/useToast';
import { useLogoSettings } from './hooks/useLogoSettings';
import type { LogoPair, LogoSource } from '@/integrations/supabase/rpc-types';

/** Anteprima in sola lettura di una coppia logo esteso + compatto. */
const LogoPreview = ({ logo, alt, emptyLabel }: { logo: LogoPair | null; alt: string; emptyLabel: string }) => (
  <div className="flex items-center justify-center gap-3 min-h-[5rem]">
    {logo?.logo_url ? (
      <img src={logo.logo_url} alt={alt} className="max-h-16 max-w-[60%] object-contain" />
    ) : (
      <div className="w-14 h-14 rounded-lg bg-gray-100 flex items-center justify-center">
        <span className="text-xs text-gray-400 text-center px-1">{emptyLabel}</span>
      </div>
    )}
    {logo?.logo_small_url && (
      <img src={logo.logo_small_url} alt={`${alt} mini`} className="max-h-8 max-w-[30%] object-contain opacity-70" />
    )}
  </div>
);

const LogoPage = () => {
  const { userData, userRole } = useAuth();
  const { t } = useI18n();
  const { settings, isLoading, uploadLogo, setLogoSource } = useLogoSettings();
  const [savingSource, setSavingSource] = useState(false);

  if (isLoading || !settings) {
    return (
      <div className="max-w-6xl mx-auto pb-4 grid grid-cols-1 md:grid-cols-2 gap-6">
        <Skeleton className="h-64 w-full rounded-xl" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  const agency = settings.agency;
  const isMember = !!agency;
  const hasOwnLogo = !!settings.own.logo_url;
  const hasAgencyLogo = !!agency?.logo_url;
  const effectiveSource = settings.effective.source;
  const isFallback = effectiveSource !== 'none' && effectiveSource !== settings.logo_source;

  const handleUpload = (variant: 'logo' | 'logo_small') => async (file: File) => {
    try {
      const url = await uploadLogo(file, variant);
      toast({
        variant: 'success',
        title: t(variant === 'logo' ? 'logoSettings.logoUpdated' : 'logoSettings.logoCompactUpdated'),
      });
      return url;
    } catch (err: any) {
      toast({ title: t('logoSettings.uploadError'), description: err.message, variant: 'destructive' });
      throw err;
    }
  };

  const handleSourceChange = async (source: LogoSource) => {
    const available = source === 'own' ? hasOwnLogo : hasAgencyLogo;
    if (source === settings.logo_source || savingSource || !available) return;
    setSavingSource(true);
    try {
      await setLogoSource(source);
      toast({ variant: 'success', title: t('logoSettings.sourceUpdated') });
    } catch (err: any) {
      toast({ title: t('logoSettings.saveError'), description: err.message, variant: 'destructive' });
    } finally {
      setSavingSource(false);
    }
  };

  const options: { value: LogoSource; label: string; logo: LogoPair; available: boolean }[] = [
    { value: 'own', label: t('logoSettings.useOwn'), logo: settings.own, available: hasOwnLogo },
    ...(agency ? [{ value: 'agency' as const, label: t('logoSettings.useAgency'), logo: agency, available: hasAgencyLogo }] : []),
  ];

  return (
    <div className="max-w-6xl mx-auto pb-4 space-y-6">
      <div className={`grid grid-cols-1 gap-6 ${isMember ? 'md:grid-cols-2' : ''}`}>
        {/* Logo proprio: caricabile da tutti */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl font-normal flex items-center gap-2">
              <Palette className="h-4 w-4" />
              {t('logoSettings.ownTitle')}
            </CardTitle>
            <p className="text-sm text-gray-500">
              {userRole === 'agency' ? t('logoSettings.ownDescriptionAgency') : t('logoSettings.ownDescription')}
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex flex-row items-start justify-around gap-6">
              <div className="flex flex-col items-center gap-4">
                <p className="text-sm font-medium">{t('logoSettings.logoLabel')}</p>
                <ImageUpload
                  showUrlInput={false}
                  showUnsplashButton={false}
                  showUploadMessage={false}
                  image={settings.own.logo_url}
                  onImageChange={() => {}}
                  uploadFn={handleUpload('logo')}
                  heightClass="h-[90px] w-[180px]"
                  disabled={!userData?.id}
                />
                <p className="text-xs text-gray-500 text-center max-w-[180px]">
                  {t('logoSettings.extendedDescription')}
                </p>
              </div>
              <div className="flex flex-col items-center gap-4">
                <p className="text-sm font-medium">{t('logoSettings.logoCompactLabel')}</p>
                <ImageUpload
                  showUrlInput={false}
                  showUnsplashButton={false}
                  showUploadMessage={false}
                  image={settings.own.logo_small_url}
                  onImageChange={() => {}}
                  uploadFn={handleUpload('logo_small')}
                  heightClass="h-[90px] w-[90px]"
                  disabled={!userData?.id}
                />
                <p className="text-xs text-gray-500 text-center max-w-[180px]">
                  {t('logoSettings.compactDescription')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logo ereditato dall'agenzia: solo lettura */}
        {agency && (
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-normal flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                {agency.name
                  ? t('logoSettings.agencyTitleNamed', { name: agency.name })
                  : t('logoSettings.agencyTitle')}
              </CardTitle>
              <p className="text-sm text-gray-500">{t('logoSettings.agencyReadOnly')}</p>
            </CardHeader>
            <CardContent>
              {hasAgencyLogo ? (
                <div className="rounded-lg border bg-gray-50 p-4">
                  <LogoPreview logo={agency} alt={agency.name ?? t('logoSettings.agencyTitle')} emptyLabel={t('logoSettings.noLogo')} />
                </div>
              ) : (
                <p className="text-sm text-gray-500">{t('logoSettings.agencyNoLogo')}</p>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Scelta: solo per chi eredita un logo */}
      {agency && (
        <Card>
          <CardHeader>
            <CardTitle className="text-xl font-normal flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4" />
              {t('logoSettings.choiceTitle')}
            </CardTitle>
            <p className="text-sm text-gray-500">{t('logoSettings.choiceDescription')}</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {options.map((option) => {
                // Un logo che non c'è non si può scegliere, e non risulta scelto
                const selected = option.available && settings.logo_source === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSourceChange(option.value)}
                    disabled={savingSource || !option.available}
                    className={`relative flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all
                      ${!option.available
                        ? 'opacity-40 cursor-not-allowed bg-gray-50 border-gray-200'
                        : selected ? 'border-black bg-gray-50 shadow-sm' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}
                      ${savingSource ? 'cursor-wait' : ''}`}
                  >
                    {selected && (
                      <div className="absolute top-2 right-2 bg-black text-white rounded-full p-0.5">
                        <Check className="h-3 w-3" />
                      </div>
                    )}
                    <LogoPreview logo={option.logo} alt={option.label} emptyLabel={t('logoSettings.noLogo')} />
                    <div className="mt-3 text-sm font-medium text-gray-700">{option.label}</div>
                    {!option.available && (
                      <div className="text-xs text-gray-400 mt-1">{t('logoSettings.notAvailable')}</div>
                    )}
                    {effectiveSource === option.value && (
                      <div className="text-xs text-emerald-600 mt-1">{t('logoSettings.inUse')}</div>
                    )}
                  </button>
                );
              })}
            </div>

            {isFallback && (
              <p className="flex items-center gap-2 text-xs text-gray-500">
                <Info className="h-3 w-3 shrink-0" />
                {effectiveSource === 'own' ? t('logoSettings.fallbackOwn') : t('logoSettings.fallbackAgency')}
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LogoPage;
